import { type Dictionary } from '@ngrx/entity';

import { type CanvasElement, type CanvasElementId } from '@blaze-runtime/engine-domain';
import { type AccessRoleUsageInfo } from '@blaze-shared/api-contracts-domain';
import { uniq } from '@blaze-shared/shared-util';
import { type Connection } from '@blaze-shared/workflow-domain';

import { type GetPropsExtractorFn } from '../../../services/element-definitions.service';
import { isElementChildOf } from '../../layout/relationship';

import { getElementPropsExtractor } from './get-element-props-extractor';

export function getLayoutAccessRoles(
  hasUsersAccessRoles: boolean,
  elements: CanvasElement[],
  connections: Connection[],
  getPropsExtractor: GetPropsExtractorFn | null,
): AccessRoleUsageInfo[] | undefined {
  if (!hasUsersAccessRoles || getPropsExtractor === null || !elements.length) {
    return undefined;
  }

  const elementsMap: Dictionary<CanvasElement> = {};
  const data: Record<CanvasElementId, (AccessRoleUsageInfo & { isMain?: boolean })[]> = {};
  const elementsWithWorkflows = new Set<CanvasElementId>();
  const elementsWithRoles = new Set<CanvasElementId>();

  // get elements' refs
  for (const element of elements) {
    const elementId = element.id;
    elementsMap[elementId] = element;
    const dataItemEntries = data[elementId] ?? [];
    const extractor = getElementPropsExtractor(element, getPropsExtractor);
    // element level vis. access roles
    const elementAccessRolesRefs = extractor?.getVisibilityAccessRolesRefs();
    const elementAccessRoles =
      elementAccessRolesRefs?.find((ref) => ref.workflowId === null && ref.roles.length)?.roles ??
      [];
    const elementReffedWfs = getElementRefedWorkflowsIds(elementId, connections);

    if (elementReffedWfs.length || elementAccessRoles.length) {
      dataItemEntries.push({
        id: elementId,
        workflowIds: elementReffedWfs,
        values: elementAccessRoles,
        isMain: true,
      });
      if (elementReffedWfs.length) {
        elementsWithWorkflows.add(elementId);
      }
      if (elementAccessRoles.length) {
        elementsWithRoles.add(elementId);
      }
    }

    // element actions (etc.) refs
    if (elementAccessRolesRefs?.length) {
      const workflowIds: string[] = [];
      for (const ref of elementAccessRolesRefs) {
        if (ref.workflowId === null) {
          continue;
        }
        if (ref.roles) {
          dataItemEntries.push({
            id: elementId,
            workflowIds: ref.workflowId ? [ref.workflowId] : [],
            values: ref.roles,
          });
          elementsWithRoles.add(elementId);
        } else if (ref.workflowId) {
          workflowIds.push(ref.workflowId);
        }
      }
      if (workflowIds.length) {
        elementsWithWorkflows.add(elementId);
        dataItemEntries.push({
          id: elementId,
          workflowIds: uniq(workflowIds),
          values: [],
        });
      }
    }
    data[elementId] = dataItemEntries;
  }

  // append children's wfs to parents' refs & collect results
  const result: AccessRoleUsageInfo[] = [];
  for (const elementId of elementsWithRoles) {
    const childrenWfs: string[] = [];
    for (const id of elementsWithWorkflows) {
      if (id === elementId) {
        continue;
      }
      const workflowIds = data[id].flatMap((ref) => ref.workflowIds);
      if (!workflowIds.length || !isElementChildOf(elementsMap, id, elementId)) {
        continue;
      }
      childrenWfs.push(...workflowIds);
    }
    const dataItem = data[elementId].find((entry) => entry.isMain);
    if (dataItem && childrenWfs.length) {
      dataItem.workflowIds = uniq([...dataItem.workflowIds, ...childrenWfs]);
    }
    // const entries = data[elementId];
    const entries = data[elementId]
      .filter(({ values }) => values.length)
      .map(({ id, values, workflowIds }) => ({
        id,
        values,
        workflowIds,
      }));
    result.push(...entries);
  }

  return result.length ? result : undefined;
}

function getElementRefedWorkflowsIds(
  elementId: CanvasElementId,
  connections: Connection[],
): string[] {
  return connections
    .filter(
      (c) =>
        // input wfs
        (c.source.layerType === 'Workflow' &&
          c.target.layerType === 'Ui' &&
          c.target.id === elementId) ||
        // output wfs
        (c.source.layerType === 'Ui' &&
          c.source.id === elementId &&
          c.target.layerType === 'Workflow' &&
          // isn't source for something else
          !connections.find(
            (wfCon) =>
              wfCon.id !== c.id &&
              wfCon.source.layerType === 'Workflow' &&
              wfCon.source.id === c.target.id,
          )),
    )
    .map((c) => (c.target.layerType === 'Ui' ? c.source.id : c.target.id));
}
